import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import AppSwitcher from "./AppSwitcher";

import { getApiUrl } from "../utils/runtimeConfig";
const API_URL = getApiUrl();

const NAV = [
  { to: "/console", label: "Overview", end: true },
  { to: "/console/domains", label: "Domains", end: true },
  { to: "/console/upload", label: "Upload", end: true },
  { to: "/console/runs", label: "Jobs", end: true },
  { to: "/console/requirements", label: "Requirements", end: true },
  { to: "/console/docs", label: "Docs", end: true },
  { to: "/console/chat", label: "Wiki Chat", end: true },
  { to: "/console/config", label: "LLM Config", end: true },
] as const;

function breadcrumb(pathname: string): string[] {
  if (pathname === "/console") return ["Overview"];
  if (pathname.startsWith("/console/chat")) return ["Wiki Chat"];
  if (pathname.startsWith("/console/runs")) return ["Jobs"];
  if (pathname.startsWith("/console/requirements")) return ["Requirements"];
  if (pathname.startsWith("/console/config")) return ["LLM Config"];
  if (pathname.startsWith("/console/domains")) return ["Domains"];
  if (pathname.startsWith("/console/upload")) return ["Upload"];
  if (pathname.startsWith("/console/docs")) return ["Docs"];
  return ["Brain"];
}

const BrainConsoleLayout: React.FC = () => {
  const { pathname } = useLocation();
  const { instance, accounts } = useMsal();
  const account = accounts[0] || instance.getActiveAccount();
  const crumbs = breadcrumb(pathname);
  const apiHost = API_URL.replace(/^https?:\/\//, "");

  return (
    <div className="flex h-screen bg-[#0d0d0a] text-[#E5E0D5] overflow-hidden">
      <aside className="w-52 flex-shrink-0 bg-[#111110] flex flex-col border-r border-[#1f1e1b]">
        <div className="px-4 py-4 border-b border-[#1f1e1b]">
          <AppSwitcher currentAppId="brain" />
        </div>

        <nav className="flex-1 py-3 text-sm">
          <div className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-[#5A5448]">
            Knowledge
          </div>
          {NAV.map(({ to, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `block mx-2 px-3 py-2 border-l-2 transition-colors ${
                  isActive
                    ? "text-[#C9A260] border-[#C9A260] bg-[#C9A260]/8"
                    : "text-[#9D9585] border-transparent hover:text-[#E5E0D5] hover:bg-white/4"
                }`
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="px-4 py-3 border-t border-[#1f1e1b] text-xs space-y-2">
          {account && (
            <div className="truncate text-[#5A5448]" title={account.username ?? ""}>
              {account.username ?? account.name}
            </div>
          )}
          <button
            type="button"
            onClick={() => instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin })}
            className="text-[#5A5448] hover:text-[#C9A260] inline-flex items-center gap-1 transition-colors"
          >
            ↩ Sign out
          </button>
          <div className="font-mono truncate text-[#3a3830]" title={API_URL}>
            {apiHost}
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-11 flex-shrink-0 bg-[#111110] border-b border-[#1f1e1b] flex items-center px-4 gap-2 text-sm">
          <span className="text-[#3a3830]">/</span>
          {crumbs.map((c, i) => (
            <span key={i} className="flex items-center gap-2">
              {i > 0 && <span className="text-[#3a3830]">/</span>}
              <span
                className={
                  i === crumbs.length - 1
                    ? "font-mono text-[#E5E0D5]"
                    : "text-[#5A5448]"
                }
              >
                {c}
              </span>
            </span>
          ))}
          <div className="ml-auto flex items-center gap-2 text-xs text-[#5A5448]">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Live sync
            </span>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-4">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default BrainConsoleLayout;
